import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PanelRightClose } from 'lucide-react';
import {
  EmailContentPaddingSchema,
  type EmailAttachment,
  type EmailCalendarInput,
  type VariableDefinitions,
} from '@blogforge/shared';
import { api } from '../hooks/api.js';
import { parseDesignOverrides } from '../helpers.js';
import { BlockInspector } from './BlockInspector.js';
import { EmailVariablesEditor } from './inspector/EmailVariablesEditor.js';
import { EmailAttachmentEditor } from './inspector/EmailAttachmentEditor.js';
import { EmailCalendarInviteEditor } from './inspector/EmailCalendarInviteEditor.js';

interface Props {
  post: Record<string, unknown> | undefined;
  postId: string;
  /**
   * 'post' (default) renders the post inspector; 'email' renders email-specific
   * fields; 'block' renders only the block editor (used by the component
   * designer, where there is no post/email entity to configure).
   */
  mode?: 'post' | 'email' | 'block';
  /** Variable names for the block pickers — see BlockInspector. */
  variableNameOptions?: string[] | undefined;
  /** When provided, shows a collapse button in the panel header. */
  onToggleCollapse?: () => void;
}

type Tab = 'block' | 'seo' | 'post' | 'email';

export function InspectorPanel({
  post,
  postId,
  mode = 'post',
  variableNameOptions,
  onToggleCollapse,
}: Props) {
  const [tab, setTab] = useState<Tab>(
    mode === 'email' ? 'email' : mode === 'block' ? 'block' : 'post',
  );
  const [paddingError, setPaddingError] = useState<string | null>(null);
  const qc = useQueryClient();

  const saveSeo = (data: Record<string, unknown>) =>
    api.posts
      .update(postId, data as never)
      .then(() => qc.invalidateQueries({ queryKey: ['post', postId] }));

  const saveEmail = (data: Record<string, unknown>) =>
    api.emails
      .update(postId, data as never)
      .then(() => qc.invalidateQueries({ queryKey: ['email', postId] }));

  const tabs: Tab[] =
    mode === 'email' ? ['email', 'block'] : mode === 'block' ? ['block'] : ['post', 'block', 'seo'];

  const emailDesignOverrides = parseDesignOverrides(post?.['designOverrides']);
  const emailBackgroundColor = emailDesignOverrides['colorBackground'] ?? '#FFFFFF';
  const emailFooterBackgroundColor = emailDesignOverrides['footerBackground'] ?? '#FFFFFF';
  const emailFooterTextColor = emailDesignOverrides['footerTextColor'] ?? '#000000';

  const updateEmailDesignOverrides = (patch: Record<string, string>) => {
    void saveEmail({ designOverrides: { ...emailDesignOverrides, ...patch } });
    // The canvas paints itself from the *resolved* design (useDocumentDesign),
    // which is its own query — the document refetch above does not refresh it.
    void qc.invalidateQueries({ queryKey: ['design', 'email', postId] });
  };

  // Blank clears the override (the theme falls back to its default padding).
  const saveContentPadding = (raw: string) => {
    const value = raw.trim();
    if (!value) {
      setPaddingError(null);
      void saveEmail({ contentPadding: null });
      return;
    }
    const parsed = EmailContentPaddingSchema.safeParse(value);
    if (!parsed.success) {
      setPaddingError(parsed.error.issues[0]?.message ?? 'Invalid padding');
      return;
    }
    setPaddingError(null);
    void saveEmail({ contentPadding: parsed.data });
  };

  const saveEmailVariables = (definitions: VariableDefinitions) =>
    api.emails
      .updateVariables(postId, definitions)
      .then(() => qc.invalidateQueries({ queryKey: ['email', postId] }));

  const saveEmailAttachments = (attachments: EmailAttachment[]) =>
    api.emails
      .update(postId, { attachments })
      .then(() => qc.invalidateQueries({ queryKey: ['email', postId] }));

  const saveEmailCalendarInvite = (invite: EmailCalendarInput) =>
    api.emails
      .updateCalendarInvite(postId, invite)
      .then(() => qc.invalidateQueries({ queryKey: ['email', postId] }));

  const clearEmailCalendarInvite = () =>
    api.emails
      .deleteCalendarInvite(postId)
      .then(() => qc.invalidateQueries({ queryKey: ['email', postId] }));

  return (
    <div className="inspector-panel">
      <div className="inspector-tabs">
        {tabs.map((t) => (
          <button
            type="button"
            key={t}
            onClick={() => setTab(t)}
            className={`inspector-tab ${tab === t ? 'inspector-tab--active' : ''}`}
          >
            {t === 'post' ? 'Post' : t === 'block' ? 'Block' : t === 'seo' ? 'SEO' : 'Email'}
          </button>
        ))}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            title="Collapse panel"
            className="text-muted hover:bg-ink/5 hover:text-ink mr-1 mb-1.5 ml-auto flex h-7 w-7 items-center justify-center rounded-lg transition-colors"
          >
            <PanelRightClose size={14} />
          </button>
        )}
      </div>

      <div className="relative flex-1 space-y-5 overflow-y-auto p-5">
        {mode === 'email' && tab === 'email' && (
          <div className="space-y-4">
            <div>
              <label className="field-label">Subject</label>
              <input
                className="field-input"
                defaultValue={(post?.['subject'] as string) ?? ''}
                onBlur={(e) => void saveEmail({ subject: e.target.value })}
                placeholder="Email subject…"
              />
            </div>
            <div>
              <label className="field-label">Preview text</label>
              <textarea
                className="field-textarea"
                rows={3}
                defaultValue={(post?.['previewText'] as string) ?? ''}
                onBlur={(e) => void saveEmail({ previewText: e.target.value })}
                placeholder="Hidden teaser shown next to the subject in inboxes…"
              />
            </div>

            <div className="border-line border-t pt-4">
              <label className="field-label">Content padding</label>
              <input
                className="field-input font-mono"
                defaultValue={(post?.['contentPadding'] as string) ?? ''}
                onBlur={(e) => saveContentPadding(e.target.value)}
                placeholder="24px 16px"
              />
              <p className={`mt-1 text-xs ${paddingError ? 'text-danger' : 'text-muted'}`}>
                {paddingError ?? 'CSS padding around the email body. Leave blank for 24px 16px.'}
              </p>
            </div>

            <div className="border-line border-t pt-4">
              <p className="field-label">Email background color</p>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  title="Background color"
                  value={emailBackgroundColor}
                  onChange={(e) => updateEmailDesignOverrides({ colorBackground: e.target.value })}
                  className="border-line-strong bg-card h-8 w-10 shrink-0 cursor-pointer rounded border p-0.5"
                />
                <input
                  className="field-input font-mono"
                  value={emailBackgroundColor}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    updateEmailDesignOverrides({ colorBackground: value });
                  }}
                  placeholder="#FFFFFF"
                />
              </div>
              <p className="text-muted mt-1 text-xs">
                Applied to the full email body — behind the header, content and footer.
              </p>
            </div>

            <div className="border-line border-t pt-4">
              <p className="field-label">Email footer background color</p>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  title="Footer background color"
                  value={emailFooterBackgroundColor}
                  onChange={(e) => updateEmailDesignOverrides({ footerBackground: e.target.value })}
                  className="border-line-strong bg-card h-8 w-10 shrink-0 cursor-pointer rounded border p-0.5"
                />
                <input
                  className="field-input font-mono"
                  value={emailFooterBackgroundColor}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    updateEmailDesignOverrides({ footerBackground: value });
                  }}
                  placeholder="#FFFFFF"
                />
              </div>
            </div>

            <div className="border-line border-t pt-4">
              <p className="field-label">Email footer text color</p>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  title="Footer text color"
                  value={emailFooterTextColor}
                  onChange={(e) => updateEmailDesignOverrides({ footerTextColor: e.target.value })}
                  className="border-line-strong bg-card h-8 w-10 shrink-0 cursor-pointer rounded border p-0.5"
                />
                <input
                  className="field-input font-mono"
                  value={emailFooterTextColor}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    updateEmailDesignOverrides({ footerTextColor: value });
                  }}
                  placeholder="#FFFFFF"
                />
              </div>
            </div>

            <div className="border-line border-t pt-4">
              <p className="field-label">Email Variables</p>

              <EmailVariablesEditor
                rawDefinitions={post?.['variableDefinition']}
                onSave={saveEmailVariables}
              />

              <p className="text-muted mt-2 text-xs">
                Define a variable once here, then reference it in any block as{' '}
                <code className="text-ink-soft font-mono">{'{{name}}'}</code>. The default is used
                whenever no value is supplied, and a required variable always has to be given one.
                Booleans and lists also wrap content as sections —{' '}
                <code className="text-ink-soft font-mono">{'{{#name}}…{{/name}}'}</code>. See the{' '}
                <a
                  href="https://mustache.github.io/mustache.5.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  Mustache syntax reference
                </a>{' '}
                for the rest.
              </p>
            </div>

            <div className="border-line border-t pt-4">
              <p className="field-label">Email Calendar Invite</p>

              <EmailCalendarInviteEditor
                rawInvite={post?.['calendarInvite']}
                onSave={saveEmailCalendarInvite}
                onDelete={clearEmailCalendarInvite}
              />

              <p className="text-muted mt-2 text-xs">
                The event recipients can accept from this email. Dates and times are entered in your
                own timezone; give a UID to replace an invite you already sent instead of adding a
                second one.
              </p>
            </div>

            <div className="border-line border-t pt-4">
              <p className="field-label">Email Attachments</p>

              <EmailAttachmentEditor
                rawAttachments={post?.['attachments']}
                onSave={saveEmailAttachments}
              />

              <p className="text-muted mt-2 text-xs">
                Files that belong to this email. The link must be a full URL the recipient can reach
                — the file is fetched from it rather than embedded in the message.
              </p>
            </div>
          </div>
        )}

        {tab === 'post' && (
          <div className="space-y-4">
            <div>
              <label className="field-label">Slug</label>
              <input
                className="field-input"
                defaultValue={(post?.['slug'] as string) ?? ''}
                onBlur={(e) => void api.posts.update(postId, { slug: e.target.value })}
              />
              <p className="text-muted mt-1 text-xs">/posts/{post?.['slug'] as string}</p>
            </div>
            <div>
              <label className="field-label">Excerpt</label>
              <textarea
                className="field-textarea"
                rows={3}
                defaultValue={(post?.['excerpt'] as string) ?? ''}
                onBlur={(e) => void api.posts.update(postId, { excerpt: e.target.value })}
                placeholder="Short post summary…"
              />
            </div>
            <div>
              <label className="field-label">Focus keyword</label>
              <input
                className="field-input"
                defaultValue={(post?.['focusKeyword'] as string) ?? ''}
                onBlur={(e) => void api.posts.update(postId, { focusKeyword: e.target.value })}
                placeholder="e.g. content marketing"
              />
            </div>
            <div>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  defaultChecked={(post?.['noIndex'] as boolean) ?? false}
                  onChange={(e) => void api.posts.update(postId, { noIndex: e.target.checked })}
                />
                <span className="text-ink-soft text-xs">Hide from search engines (noindex)</span>
              </label>
            </div>
          </div>
        )}

        {tab === 'block' && (
          <BlockInspector post={post} mode={mode} variableNameOptions={variableNameOptions} />
        )}

        {tab === 'seo' && (
          <div className="space-y-4">
            <div>
              <label className="field-label">
                SEO title ({((post?.['seoTitle'] as string) ?? '').length}/60)
              </label>
              <input
                className="field-input"
                maxLength={60}
                defaultValue={(post?.['seoTitle'] as string) ?? ''}
                onBlur={(e) => void saveSeo({ seoTitle: e.target.value })}
                placeholder={post?.['title'] as string}
              />
            </div>
            <div>
              <label className="field-label">
                Meta description ({((post?.['seoDescription'] as string) ?? '').length}/160)
              </label>
              <textarea
                className="field-textarea"
                rows={3}
                maxLength={160}
                defaultValue={(post?.['seoDescription'] as string) ?? ''}
                onBlur={(e) => void saveSeo({ seoDescription: e.target.value })}
                placeholder="Describe this post in 150–160 characters…"
              />
            </div>
            {/* SERP preview */}
            <div>
              <p className="field-label">Google preview</p>
              <div className="border-line bg-card rounded-lg border p-3">
                <p className="text-ok truncate text-xs">
                  yoursite.com › posts › {(post?.['slug'] as string) ?? ''}
                </p>
                <p className="text-primary mt-0.5 truncate text-sm font-medium">
                  {(post?.['seoTitle'] as string) || (post?.['title'] as string) || 'Post title'}
                </p>
                <p className="text-muted mt-0.5 line-clamp-2 text-xs">
                  {(post?.['seoDescription'] as string) || 'No meta description — add one above.'}
                </p>
              </div>
            </div>
            <div>
              <label className="field-label">Canonical URL</label>
              <input
                className="field-input"
                defaultValue={(post?.['canonicalUrl'] as string) ?? ''}
                onBlur={(e) => void saveSeo({ canonicalUrl: e.target.value || null })}
                placeholder="Auto-generated if blank"
              />
            </div>
            <div>
              <label className="field-label">Schema type</label>
              <select
                title="Select Schema type"
                className="field-input"
                defaultValue={(post?.['schemaType'] as string) ?? 'BlogPosting'}
                onChange={(e) => void saveSeo({ schemaType: e.target.value })}
              >
                <option value="BlogPosting">BlogPosting</option>
                <option value="Article">Article</option>
                <option value="HowTo">HowTo</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
